import { type Diagnostic, type ValidationRule } from '../types';

export type StepLike = {
  id: string;
  stepKind: string;
  steps?: StepLike[];
};

export type CommonRuleError = { stepId: string; error: string };

export type RoutineContext = {
  routineSpace?: Array<{ id: string; name: string }>;
  globalSpace?: Array<{ id: string; name: string }>;
  routineVariables?: Array<{ id: string; name: string }>;
  globalVariables?: Array<{ id: string; name: string }>;
};

function validateNoDuplicateNames(
  globals: Array<{ id: string; name: string }> | undefined,
  all: Array<{ id: string; name: string }> | undefined,
  kind: 'space' | 'variable',
): CommonRuleError[] {
  if (!globals || !all) return [];

  const out: CommonRuleError[] = [];

  for (const g of globals) {
    const hasDup = all.some((a) => a.name === g.name && a.id !== g.id);

    if (hasDup) {
      out.push({
        stepId: `__${kind}__:${g.name}`,
        error:
          kind === 'space'
            ? 'Duplicate Space name between global and routine'
            : 'Duplicate Environment Variable name between global and routine',
      });
    }
  }

  return out;
}

export function runCommonRoutineRules(ctx?: RoutineContext): CommonRuleError[] {
  let out: CommonRuleError[] = [];

  if (ctx) {
    const allSpace = [...(ctx.globalSpace ?? []), ...(ctx.routineSpace ?? [])];

    out = out.concat(
      validateNoDuplicateNames(ctx.globalSpace, allSpace, 'space'),
    );

    const allVars = [
      ...(ctx.globalVariables ?? []),
      ...(ctx.routineVariables ?? []),
    ];

    out = out.concat(
      validateNoDuplicateNames(ctx.globalVariables, allVars, 'variable'),
    );
  }

  return out;
}

export const commonRulesValidationRule: ValidationRule = {
  id: 'common.rules',
  appliesTo: 'routine',
  run: ({ routine, ctx }) => {
    const errors = runCommonRoutineRules({
      routineSpace: routine.space,
      globalSpace: ctx.globalSpace,
      routineVariables: routine.environmentVariables,
      globalVariables: ctx.globalVariables,
    });

    const diagnostics: Diagnostic[] = errors.map((e) => ({
      code: 'common.rule',
      message: e.error,
      severity: 'error',
      stepId: e.stepId,
      sourceRule: 'common.rules',
    }));

    return diagnostics;
  },
};
